import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, Phone } from "lucide-react";
import CarCard from "../../components/public/CarCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const txt = (v, fallback) => (v && v.trim() ? v : fallback);
const has = (v) => typeof v === "string" && v.trim().length > 0;

export default function Home() {
  const [rentals, setRentals] = useState([]);
  const [sales, setSales] = useState([]);
  const [siteInfo, setSiteInfo] = useState({});

  useEffect(() => {
    axios.get(`${API}/public/cars?type=rent`).then((r) => {
      const all = r.data.cars || [];
      // Kun ledige biler (aldrig udlejede) må vises på forsiden.
      // Regel: Carbuddi (langtidsleje) har altid prioritet. Hvis der findes MINDST én ledig Carbuddi-bil,
      // vises kun Carbuddi. Er der INGEN ledige Carbuddi-biler, vises GoMore-biler i stedet.
      const availableCarbuddi = all.filter((c) => !c.is_gomore_public && !c.is_rented_out);
      const availableGomore = all.filter((c) => c.is_gomore_public && !c.is_rented_out);
      const picked = availableCarbuddi.length > 0
        ? availableCarbuddi.slice(0, 4)
        : availableGomore.slice(0, 4);
      setRentals(picked);
    }).catch(() => {});
    axios.get(`${API}/public/cars?type=sale`).then((r) => setSales(r.data.cars.slice(0, 4))).catch(() => {});
    axios.get(`${API}/public/site-info`).then((r) => setSiteInfo(r.data)).catch(() => {});
  }, []);

  const hasHero = has(siteInfo.hero_image_url);
  const hasAboutImg = has(siteInfo.about_image_url);

  const aboutSnippetText = txt(
    siteInfo.about_snippet_text,
    "Mahasa tilbyder biludlejning og salg af brugte biler med fokus på kvalitet, fleksibilitet og en god kundeoplevelse."
  );

  return (
    <>
      {/* HERO */}
      <section className="relative min-h-[640px] sm:min-h-[720px] -mt-16 sm:-mt-20 flex items-center bg-graphite" data-testid="home-hero">
        {hasHero ? (
          <div className="absolute inset-0">
            <img src={siteInfo.hero_image_url} alt="" className="w-full h-full object-cover opacity-80" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/20" />
          </div>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-graphite to-black" />
        )}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-white py-20">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="max-w-3xl"
          >
            <div className="label-tag text-copper mb-6">{txt(siteInfo.hero_label, "Mahasa")}</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight mb-6">
              {txt(siteInfo.hero_title_line1, "Biludlejning & Bilsalg")}<br/>
              <span className="text-white/80">{txt(siteInfo.hero_title_line2, "til gode priser.")}</span>
            </h1>
            <p className="font-body text-base sm:text-lg lg:text-xl text-white/75 mb-10 font-light max-w-2xl leading-relaxed whitespace-pre-line">
              {txt(siteInfo.hero_subtitle, "Fleksibel biludlejning og biler til salg med fokus på gennemsigtighed, service og attraktive priser.")}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link to="/biludlejning" className="btn-primary" data-testid="hero-rentals-btn">
                {txt(siteInfo.hero_cta_rent_label, "Se udlejnings­biler")} <ArrowRight size={18} strokeWidth={1.8} />
              </Link>
              <Link to="/til-salg" className="btn-outline-white" data-testid="hero-sales-btn">
                {txt(siteInfo.hero_cta_sale_label, "Se biler til salg")}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICES + WHY sections fjernet — forstyrrede mere end de gavnede */}

      {/* FEATURED CARS */}
      {(rentals.length > 0 || sales.length > 0) && (
        <section className="py-20 md:py-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="home-featured">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="label-tag mb-3">{txt(siteInfo.featured_label, "Udvalgte biler")}</div>
              <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-graphite">{txt(siteInfo.featured_title, "Klar til at køre.")}</h2>
            </div>
          </div>
          {rentals.length > 0 && (
            <div className="mb-14">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading text-2xl text-graphite">Til udlejning</h3>
                <Link to="/biludlejning" className="text-copper hover:text-copper-dark font-medium text-sm inline-flex items-center gap-1.5">Se alle <ArrowRight size={14}/></Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
                {rentals.map((c) => <CarCard key={c.license_plate} car={c} type="rent" />)}
              </div>
            </div>
          )}
          {sales.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading text-2xl text-graphite">Til salg</h3>
                <Link to="/til-salg" className="text-copper hover:text-copper-dark font-medium text-sm inline-flex items-center gap-1.5">Se alle <ArrowRight size={14}/></Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
                {sales.map((c) => <CarCard key={c.license_plate} car={c} type="sale" />)}
              </div>
            </div>
          )}
        </section>
      )}

      {/* ABOUT SNIPPET */}
      <section className="bg-white py-20 md:py-28" data-testid="home-about">
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 ${hasAboutImg ? "lg:grid-cols-2 gap-12" : "max-w-3xl"} items-center`}>
          <div>
            <div className="label-tag mb-4">{txt(siteInfo.about_snippet_label, "Om Mahasa")}</div>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-graphite mb-6 leading-tight">
              {txt(siteInfo.about_snippet_title, "Kvalitet og fleksibilitet i hverdagen.")}
            </h2>
            <p className="font-body text-base md:text-lg text-graphite/75 mb-6 leading-relaxed whitespace-pre-line">
              {aboutSnippetText}
            </p>
            <Link to="/om-os" className="btn-outline" data-testid="home-about-link">
              Læs vores historie
            </Link>
          </div>
          {hasAboutImg && (
            <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-graphite/10">
              <img src={siteInfo.about_image_url} alt="" className="w-full h-full object-cover" />
            </div>
          )}
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="bg-graphite text-white py-20 md:py-28" data-testid="home-contact-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="label-tag text-copper mb-4">{txt(siteInfo.cta_label, "Spørgsmål?")}</div>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mb-6">{txt(siteInfo.cta_title, "Vi er klar til at hjælpe.")}</h2>
          <p className="text-white/70 mb-10 max-w-xl mx-auto whitespace-pre-line">{txt(siteInfo.cta_subtitle, "Skriv eller ring — vi vender altid hurtigt tilbage.")}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <Link to="/kontakt" className="btn-primary" data-testid="home-contact-btn">
              Kontakt os <ArrowRight size={18} />
            </Link>
            {siteInfo.contact_phone && (
              <a href={`tel:${siteInfo.contact_phone}`} className="inline-flex items-center gap-2 text-white/85 hover:text-copper transition-colors text-base">
                <Phone size={16}/> {siteInfo.contact_phone}
              </a>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
